import { useCallback, useMemo, useState } from 'react'
import { Music, Trash2, Star, StarOff } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { StatusBadge } from '@/components/StatusBadge'
import { useToast } from '@/providers/UiProviders'
import { fetchSongs, setSongPremium, deleteSong, type SongRow } from './mediaApi'

const statusVariant = (s: string): 'active' | 'pending' | 'deleted' =>
  s === 'approved' ? 'active' : s === 'pending' ? 'pending' : 'deleted'

/**
 * MUSIC.01 — MP3 katalog (tz/mp3-tv). Barcha treklar (foydalanuvchi yuklamalari +
 * katalog); premium-flag toggle + o'chirish. Status ustuni bilan.
 */
export function MusicCatalog() {
  const { toast } = useToast()
  const [reloadKey, setReloadKey] = useState(0)

  const run = useCallback(
    async (fn: () => Promise<unknown>) => {
      try {
        await fn()
        toast('Готово', { tone: 'success' })
        setReloadKey((k) => k + 1)
      } catch (e) {
        toast('Ошибка', { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) })
      }
    },
    [toast],
  )

  const columns = useMemo<AdminColumn<SongRow>[]>(
    () => [
      {
        key: 'title',
        header: 'Трек',
        type: 'text',
        minWidth: 220,
        render: (r) => (
          <span className="flex items-center gap-2">
            <Music size={14} className="text-tertiary" />
            <span className="font-medium text-primary">{r.title}</span>
            {r.is_premium ? <span className="text-xs text-amber-500">Premium</span> : null}
          </span>
        ),
      },
      { key: 'artist', header: 'Исполнитель', type: 'text', width: 160 },
      { key: 'plays', header: 'Прослуш.', type: 'number', width: 100, align: 'right' },
      {
        key: 'status',
        header: 'Статус',
        type: 'text',
        width: 110,
        render: (r) => <StatusBadge status={statusVariant(r.status)} label={r.status} />,
      },
    ],
    [],
  )

  const rowActions = useMemo<RowAction<SongRow>[]>(
    () => [
      {
        id: 'premium-on',
        label: 'Сделать Premium',
        icon: <Star size={13} />,
        visible: (r) => !r.is_premium,
        onSelect: (r) => void run(() => setSongPremium(r.id, true)),
      },
      {
        id: 'premium-off',
        label: 'Убрать Premium',
        icon: <StarOff size={13} />,
        visible: (r) => r.is_premium,
        onSelect: (r) => void run(() => setSongPremium(r.id, false)),
      },
      { id: 'delete', label: 'Удалить', icon: <Trash2 size={13} />, danger: true, onSelect: (r) => void run(() => deleteSong(r.id)) },
    ],
    [run],
  )

  return (
    <>
      <PageHeader title="Музыка — каталог" screenId="MUSIC.01" />
      <DataTable<SongRow>
        key={reloadKey}
        screenId="MUSIC.01"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetchSongs}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        emptyState={{ icon: <StarOff size={22} />, title: 'Пока нет треков' }}
      />
    </>
  )
}
